import { useEffect, useState } from "react";
import Button from "../../common/button";
import VideoPlayer from "./VideoPlayer";
import {
  useLazyGetAcademyCourseModuleDetailsQuery,
  useLazyGetAcademyCourseModuleEpisodeDetailsQuery,
  useLazyGetAcademyHomeDataQuery,
  useLazyGetQuizDetailsQuery,
} from "../../../store/api/academyApi";
import { Link, useLocation, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setDropdownList, setPage } from "../../../store/slices/academySlice";
import {
  AcademyQuizLogoIcons,
  BackButtonIcon,
  DownArrowIcon,
} from "../../../icons/academy-icons";
import Quiz from "./Quiz";
import { MixpanelEvent, trackEvent } from "../../../analytics/mixpanel";

export interface EpisodeData {
  episode_number: string;
  episode_id: string;
  quiz_id: string;
  tool_download_link: string;
  title: string;
  episode_completion_status: string;
  description: string;
}

const ModuleComponent = () => {
  const [dropdownListEpisodes, setDropdownListEpisodes] = useState<any>([]);
  const { course_id, module_id, episode_id } = useParams<any>();
  const location = useLocation();
  const dispatch = useDispatch();
  const [courseData, setCourseData] = useState<any>(null);
  const [episodeData, setEpisodeData] = useState<EpisodeData | null>(null);
  const [quizData, setQuizData] = useState<any>(null);
  const [quizPage, setQuizPage] = useState<boolean>(false);
  const [modules, setModules] = useState<any>(null);
  const [triggerGetAcademyCourseModuleEpisodeDetailsQueryData] =
    useLazyGetAcademyCourseModuleEpisodeDetailsQuery();
  const [triggerGetAcademyCourseModuleDetailsQueryData] =
    useLazyGetAcademyCourseModuleDetailsQuery();
  const [triggerQuizDetailsQueryData] = useLazyGetQuizDetailsQuery();

  const [triggerGetAcademyAllTabData] = useLazyGetAcademyHomeDataQuery();

  const getQuizData = async () => {
    try {
      if (!episodeData?.quiz_id) return;
      const response = await triggerQuizDetailsQueryData({
        quizId: episodeData?.quiz_id,
      })?.unwrap();
      if (response?.success) {
        setQuizData(response.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getEpisodeData = async () => {
    try {
      if (!modules) return;
      const resp = await triggerGetAcademyCourseModuleEpisodeDetailsQueryData({
        episodeId: episode_id,
      }).unwrap();
      if (resp?.success) {
        setEpisodeData(resp.data);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const getModuleData = async () => {
    try {
      const response = await triggerGetAcademyCourseModuleDetailsQueryData({
        moduleId: module_id,
      })?.unwrap();
      if (response?.success) {
        setModules(response.data.episodes);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCourseData = async () => {
    try {
      const response = await triggerGetAcademyAllTabData({
        type: "course",
      })?.unwrap();
      if (response?.success) {
        const courses = response.data?.courses_list?.map((course: any) => ({
          title: course.title,
          id: course.course_id,
        }));
        setCourseData(courses);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateDropdownList = () => {
    const moduleDropdown = modules?.map((episode: any) => ({
      title: episode.episode_number + ". " + episode.title,
      id: episode.episode_id
        ? `${course_id}/${module_id}/${episode.episode_id}`
        : ``,
    }));

    const updatedDropdown = courseData?.map((course: any) =>
      course.id === course_id
        ? { ...course, dropdown: moduleDropdown }
        : course,
    );

    setDropdownListEpisodes(updatedDropdown);
  };

  const sendAnalytics = (downloadLink: string) => {
    fetch(downloadLink, { mode: "no-cors" })
      .then((response) => {
        if (response.ok) {
          trackEvent(MixpanelEvent.TOOLS_DOWNLOAD_PRESS, {
            episode_id: episode_id,
            status: "success",
          });
        } else {
          trackEvent(MixpanelEvent.TOOLS_DOWNLOAD_PRESS, {
            episode_id: episode_id,
            status: "failed",
          });
        }
      })
      .catch(() => {
        trackEvent(MixpanelEvent.TOOLS_DOWNLOAD_PRESS, {
          episode_id: episode_id,
          status: "failed",
        });
      });
  };

  useEffect(() => {
    getCourseData();
    setQuizPage(false);
  }, [location.pathname]);

  useEffect(() => {
    getModuleData();
  }, [courseData, module_id]);

  useEffect(() => {
    getEpisodeData();
  }, [modules, location.pathname]);

  useEffect(() => {
    getQuizData();
  }, [episodeData]);

  useEffect(() => {
    if (modules?.length && courseData) {
      setDropdownListEpisodes([]);
      updateDropdownList();
    }
  }, [modules, courseData]);

  useEffect(() => {
    if (dropdownListEpisodes?.length) {
      dispatch(setDropdownList(dropdownListEpisodes));
      dispatch(setPage("module"));
    }
  }, [dropdownListEpisodes]);

  return (
    <>
      {!quizPage ? (
        <div className="flex flex-col gap-6">
          <div className="font-bold text-3xl flex gap-6 items-center">
            <Link to={location.pathname.split("/").slice(0, -2).join("/")}>
              <BackButtonIcon width={20} height={20} />
            </Link>
            {episodeData ? (
              <p>{episodeData?.episode_number + ". " + episodeData?.title}</p>
            ) : (
              ""
            )}
          </div>
          <div>
            <VideoPlayer
              key={episode_id}
              episodeData={episodeData}
              episode_id={episode_id || ""}
            />
          </div>
          <div className="flex justify-between items-center">
            <div className="w-1/2">
              {episodeData?.description && (
                <>
                  <h1 className="text-xl font-bold">Description</h1>
                  <h4 className="text-md text-[#404040]">
                    {episodeData?.description}
                  </h4>
                </>
              )}
            </div>
            {episodeData?.tool_download_link && (
              <Button
                customClasses="rounded-xl px-16 py-3"
                onClick={() => {
                  const downloadLink = episodeData.tool_download_link;
                  window.open(downloadLink);
                  sendAnalytics(downloadLink);
                }}
              >
                {" "}
                <div className="flex items-center gap-2">
                  {" "}
                  <DownArrowIcon /> <p className="font-bold">Download Tool</p>
                </div>
              </Button>
            )}
          </div>
          {quizData && (
            <div className="w-full bg-[#E7ECFC] flex justify-between items-center rounded-lg px-4">
              <div className="my-12 mx-4">
                <h1 className="text-xl font-bold">{episodeData?.title} Quiz</h1>
                <h4 className="text-[#404040] flex gap-2 my-2">
                  <p>Answer all the Questions and get</p>{" "}
                  <p className="font-bold">XP</p>
                </h4>
                <Button
                  customClasses="rounded-xl px-16 py-3 font-bold mt-4"
                  onClick={() => setQuizPage(true)}
                >
                  Take a Quiz
                </Button>
              </div>
              <div className="relative w-80 h-56 m-8">
                <img
                  src="https://dev-profile-pictures-onepercentclub.s3.ap-south-1.amazonaws.com/academy/star-icon-512.png"
                  className="w-40 absolute top-4 left-20"
                />
                <AcademyQuizLogoIcons />
              </div>
            </div>
          )}
        </div>
      ) : (
        <Quiz key={episode_id} quizData={quizData} episodeData={episodeData} />
      )}
    </>
  );
};

export default ModuleComponent;
